"use client";

import { useEffect } from "react";

const STORAGE_KEY = "js_lead_attribution";

export interface StoredAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  referrer?: string;
  landing_page?: string;
  lead_mode?: "REAL" | "TEST";
  captured_at?: string;
}

export function getStoredAttribution(): StoredAttribution {
  if (typeof window === "undefined") return {};

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: StoredAttribution = raw ? JSON.parse(raw) : {};

    // Ensure landing page is set
    if (!parsed.landing_page) {
      parsed.landing_page = window.location.pathname;
    }

    return parsed;
  } catch {
    return {
      landing_page: typeof window !== "undefined" ? window.location.pathname : undefined,
    };
  }
}

export default function UTMTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const existing = getStoredAttribution();

      const utmSource = urlParams.get("utm_source");
      const utmMedium = urlParams.get("utm_medium");
      const utmCampaign = urlParams.get("utm_campaign");
      const utmContent = urlParams.get("utm_content");
      const utmTerm = urlParams.get("utm_term");
      const gclid = urlParams.get("gclid");
      const mode = urlParams.get("mode");

      // Check if URL has any marketing or mode params
      if (utmSource || utmCampaign || gclid || mode) {
        const attribution: StoredAttribution = {
          ...existing,
          utm_source: utmSource || existing.utm_source || (gclid ? "google_ads" : undefined),
          utm_medium: utmMedium || existing.utm_medium || (gclid ? "cpc" : undefined),
          utm_campaign: utmCampaign || existing.utm_campaign,
          utm_content: utmContent || existing.utm_content,
          utm_term: utmTerm || existing.utm_term,
          gclid: gclid || existing.gclid,
          referrer: document.referrer || existing.referrer,
          landing_page: existing.landing_page || window.location.pathname,
          lead_mode: mode === "test" ? "TEST" : existing.lead_mode || "REAL",
          captured_at: existing.captured_at || new Date().toISOString(),
        };

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
      } else if (!existing.landing_page) {
        // First visit without params: record landing page and referrer
        const attribution: StoredAttribution = {
          landing_page: window.location.pathname,
          referrer: document.referrer || undefined,
          captured_at: new Date().toISOString(),
          lead_mode: "REAL",
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
      }
    } catch (err) {
      console.warn("[UTMTracker] Could not save attribution:", err);
    }
  }, []);

  return null;
}
